import { useEffect, useState } from 'react';
import { getEloCategories } from '@/api/elo/eloApi';
import type { EloCategory } from '@/components/tournament/models/elo.model';

export interface EloCategoriesState {
  categories: EloCategory[];
  loading: boolean;
}

interface LoadedEloCategories {
  sportUid: string | undefined;
  categories: EloCategory[];
}

/**
 * Loads Elo category bands for a sport from the API, exposing a loading flag so callers
 * can tell "still fetching" from "there are none" — both are an empty array.
 *
 * The bands are tagged with the sport they were fetched for, so switching sports reports
 * loading (and an empty list) on that very render: callers never see a frame of the
 * previous sport's bands, nor one that looks like an empty result.
 */
export function useEloCategoriesState(
  tournamentSportUid: string | undefined,
): EloCategoriesState {
  const [loaded, setLoaded] = useState<LoadedEloCategories>({
    sportUid: undefined,
    categories: [],
  });

  useEffect(() => {
    if (!tournamentSportUid) {
      setLoaded({ sportUid: undefined, categories: [] });
      return;
    }
    let cancelled = false;
    getEloCategories(tournamentSportUid)
      .then((data) => {
        if (!cancelled) {
          setLoaded({ sportUid: tournamentSportUid, categories: data ?? [] });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoaded({ sportUid: tournamentSportUid, categories: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentSportUid]);

  const isCurrentSport = loaded.sportUid === tournamentSportUid;

  return {
    categories: isCurrentSport ? loaded.categories : [],
    loading: Boolean(tournamentSportUid) && !isCurrentSport,
  };
}

/**
 * Loads Elo category bands for a sport from the API.
 * Returns an empty array when `tournamentSportUid` is missing, while loading, or on
 * fetch failure. Use {@link useEloCategoriesState} when those cases must be told apart.
 */
export function useEloCategories(
  tournamentSportUid: string | undefined,
): EloCategory[] {
  return useEloCategoriesState(tournamentSportUid).categories;
}
